<script lang="ts">
  import { Info, ArrowRight } from 'lucide-svelte'

  let { csrfToken, privacyPolicyUrl, termsOfServiceUrl }: {
    csrfToken: string,
    privacyPolicyUrl: string,
    termsOfServiceUrl: string,
  } = $props()

  let reminders = $state('')
  let smsConsent = $state(false)
  let requiresSmsConsent = $derived(reminders === 'text' || reminders === 'both')

  let age = $state('')
  let identification = $state('')
  let preferredName = $state('')
  let displayName = $state('')
  let displayNameTouched = $state(false)

  let isUnder13 = $derived(age === 'under-13')
  let isMinor = $derived(age === 'minor')

  $effect(() => {
    if (isMinor && identification !== 'anonymous') {
      identification = 'anonymous'
    }
  })

  $effect(() => {
    if (!displayNameTouched) {
      displayName = preferredName
    }
  })
</script>

<div class="bg-white p-6 sm:p-10 rounded-2xl shadow-sm border border-gray-200 w-full max-w-2xl text-left">
  <h2 class="text-2xl font-serif text-gray-900 mb-8 border-b border-gray-100 pb-4">Join the challenge</h2>

  <form method="post" class="space-y-8">
    <input type="hidden" name="authenticity_token" value={csrfToken}>

    <fieldset class="space-y-3">
      <legend class="block text-sm font-semibold text-gray-900">How old are you?</legend>
      <div class="space-y-2">
        <label class="flex items-center space-x-3 cursor-pointer p-2 hover:bg-gray-50 rounded-lg -ml-2 transition-colors">
          <input type="radio" name="signup[age]" value="adult" required class="text-blue-900 focus:ring-blue-900" bind:group={age}>
          <span class="text-gray-700">18 or older</span>
        </label>
        <label class="flex items-center space-x-3 cursor-pointer p-2 hover:bg-gray-50 rounded-lg -ml-2 transition-colors">
          <input type="radio" name="signup[age]" value="minor" required class="text-blue-900 focus:ring-blue-900" bind:group={age}>
          <span class="text-gray-700">13–17</span>
        </label>
        <label class="flex items-center space-x-3 cursor-pointer p-2 hover:bg-gray-50 rounded-lg -ml-2 transition-colors">
          <input type="radio" name="signup[age]" value="under-13" required class="text-blue-900 focus:ring-blue-900" bind:group={age}>
          <span class="text-gray-700">Under 13</span>
        </label>
      </div>
    </fieldset>

    {#if isUnder13}
      <div class="bg-amber-50 border border-amber-100 rounded-lg p-4 flex items-start gap-3">
        <Info class="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p class="text-sm text-amber-800 leading-snug">
          This service is not intended for children under age 13. If you would like to participate, ask a parent to sign up and share the readings with you.
        </p>
      </div>
    {:else}
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="space-y-2">
          <label for="phone" class="block text-sm font-semibold text-gray-900">What&rsquo;s your cell phone number?</label>
          <input type="tel" id="phone" name="signup[phone]" required={requiresSmsConsent} autocomplete="tel" class="w-full rounded-lg border-gray-300 border shadow-sm focus:border-blue-900 focus:ring-blue-900 p-3" placeholder="(555) 555-5555">
        </div>
        <div class="space-y-2">
          <label for="email" class="block text-sm font-semibold text-gray-900">What&rsquo;s your email address?</label>
          <input type="email" id="email" name="signup[email]" required autocomplete="email" class="w-full rounded-lg border-gray-300 border shadow-sm focus:border-blue-900 focus:ring-blue-900 p-3" placeholder="you@example.com">
        </div>
      </div>

      <fieldset class="space-y-3">
        <legend class="block text-sm font-semibold text-gray-900">How would you like to receive reminders?</legend>
        <div class="space-y-2">
          <label class="flex items-center space-x-3 cursor-pointer p-2 hover:bg-gray-50 rounded-lg -ml-2 transition-colors">
            <input type="radio" name="signup[reminders]" value="text" required class="text-blue-900 focus:ring-blue-900" bind:group={reminders}>
            <span class="text-gray-700">Text message</span>
          </label>
          <label class="flex items-center space-x-3 cursor-pointer p-2 hover:bg-gray-50 rounded-lg -ml-2 transition-colors">
            <input type="radio" name="signup[reminders]" value="email" required class="text-blue-900 focus:ring-blue-900" bind:group={reminders}>
            <span class="text-gray-700">Email</span>
          </label>
          <label class="flex items-center space-x-3 cursor-pointer p-2 hover:bg-gray-50 rounded-lg -ml-2 transition-colors">
            <input type="radio" name="signup[reminders]" value="both" required class="text-blue-900 focus:ring-blue-900" bind:group={reminders}>
            <span class="text-gray-700">Both</span>
          </label>
        </div>

        <fieldset disabled={!requiresSmsConsent} class="bg-gray-50 border disabled:hidden border-gray-200 rounded-lg p-4 mt-3 space-y-3">
          <label class="flex items-start gap-3 cursor-pointer">
            <input type="hidden" name="signup[sms_consent]" value="0">
            <input type="checkbox" name="signup[sms_consent]" value="1" required bind:checked={smsConsent} class="mt-1 text-blue-900 focus:ring-blue-900 rounded">
            <span class="text-sm text-gray-700 leading-snug">I agree to receive recurring automated text messages from challenge.BIBLE at the phone number I provided. Message frequency varies. Message &amp; data rates may apply. Reply STOP to cancel, HELP for help.</span>
          </label>
          <p class="text-xs text-gray-500 ml-7">See our <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer" class="underline hover:text-gray-700">Privacy Policy</a> and <a href={termsOfServiceUrl} target="_blank" rel="noopener noreferrer" class="underline hover:text-gray-700">Terms of Service</a>.</p>
        </fieldset>
      </fieldset>

      <div class="space-y-2">
        <label for="preferred_name" class="block text-sm font-semibold text-gray-900">How should the reminder messages refer to you?</label>
        <input type="text" id="preferred_name" name="signup[preferred_name]" bind:value={preferredName} autocomplete="given-name" class="w-full rounded-lg border-gray-300 border shadow-sm focus:border-blue-900 focus:ring-blue-900 p-3" placeholder="e.g. Cecelia, Mr. Smith">
      </div>

      <fieldset class="space-y-3">
        <legend class="block text-sm font-semibold text-gray-900">How would you like to be identified in the challenge activity feed?</legend>
        <div class="space-y-2">
          <label class="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg -ml-2 transition-colors" class:opacity-50={isMinor}>
            <input type="radio" name="signup[identification]" value="name" disabled={isMinor} class="text-blue-900 focus:ring-blue-900 mt-1 self-start" bind:group={identification}>
            <div class="grow flex flex-col sm:flex-row sm:items-center gap-2">
              <span class="text-gray-700 whitespace-nowrap pt-1 sm:pt-0">By name:</span>
              <input type="text" name="signup[display_name]" bind:value={displayName} onfocus={()=>displayNameTouched = true} disabled={identification != "name"} autocomplete="name" class="grow rounded-md border-gray-300 border shadow-sm focus:border-blue-900 focus:ring-blue-900 py-1.5 px-3 text-sm disabled:opacity-50" placeholder="Name or nickname">
            </div>
          </label>
          <label class="flex items-center space-x-3 cursor-pointer p-2 hover:bg-gray-50 rounded-lg -ml-2 transition-colors">
            <input type="radio" name="signup[identification]" value="anonymous" class="text-blue-900 focus:ring-blue-900" bind:group={identification}>
            <span class="text-gray-700">Anonymously</span>
          </label>
        </div>
      </fieldset>

      <div class="pt-4">
        <button type="submit" class="w-full flex items-center justify-center px-6 py-4 border border-transparent text-lg font-medium rounded-lg text-white bg-blue-900 hover:bg-blue-800 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900">
          Join the challenge
          <ArrowRight class="w-5 h-5 ml-2" />
        </button>
      </div>
    {/if}
  </form>
</div>
